import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

const CarbonCountPage = lazy(() => import('./CarbonCountPage'));
const ParadyesPage    = lazy(() => import('./ParadyesPage'));
const BunavPage       = lazy(() => import('./BunavPage'));
const EmberPage       = lazy(() => import('./EmberPage'));
const AnvayaPage      = lazy(() => import('./AnvayaPage'));
const FerrariPage     = lazy(() => import('./FerrariPage'));

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const COLORS = {
  bg: '#0E0C0A',
  bgAlt: '#0F0E0C',
  surface: '#141412',
  textPrimary: '#EDEAE3',
  textSecondary: '#6B6860',
  textMuted: '#58564f',
  textVeryMuted: '#444240',
  accent: '#D81060',
  border: 'rgba(255,255,255,0.08)',
};

const THINKER_ASCII = `
              _...._
           .-'      '-.
          /  ,-~~~~-.  \\
         /  / .-~~-. \\  \\
        |  | ( o  o ) |  |
        |  |  \\ -- /  |  |
        |  |  ,\`--',  |  |
         \\ | / ,--. \\ | /
          \\|/ / ,-, \\ \\|/
           |  | | | |  |
           |,-' | | '-,|
          /|    | |    |\\
         / |  ,-' '-.  | \\
        /  | /       \\ |  \\
       /  /|/   ,-.   \\|\\  \\
      /  / /   /   \\   \\ \\  \\
     /  / /   | ,-. |   \\ \\  \\
    /  / /    |/   \\|    \\ \\  \\
   /  / /     /     \\     \\ \\  \\
  /  / /     /  ,-.  \\     \\ \\  \\
 /  / /  ,--/ ,'   '. \\--,  \\ \\  \\
|  / / ,' / //       \\\\ \\ '.  \\ \\ |
|  | | |  ///    O    \\\\\\ |  |  | ||
|  | | | ///           \\\\| |  |  | ||
|  \\ \\ \\///             \\/ /  /  / /|
|   \\ \\ \\/     ,--,     / / /  / / |
\\    \\ \\ \\    /    \\   / / /  / /  /
 \\    \\ \\ \\  /      \\ / / /  / /  /
  \\    \\ \\ \\/   ,--, V / /  / /  /
   \\    \\ \\ \\  /    \\/ / /  / /  /
    \\    \\ '-\\/      / / '--' /  /
     \\    \\   \\     / /      /  /
      \\    \\   '---' /      /  /
       \\    \\       /    ,-'  /
        \\    '-----'   ,'    /
         \\            /     /
          \\          / ,---'
           \\        / /
            \\      / /
             \\    / /
              \\  / /
    ,-----------\\/ /----------,
   /             \\/            \\
  /  . ' . ' . '  ' . ' . ' .  \\
 /                               \\
/   . ' . ' . ' . ' . ' . ' . '  \\
\\________________________________/
  \\                            /
   \\........................../ `;

const ROTATING_WORDS = [
  'idea',
  'question',
  'system',
  'experience',
  'design',
];

const PROJECTS = [
  {
    id: 'bunav',
    name: 'Bunav — Parenting App',
    categoryLabel: 'Product Design',
    type: 'App Design & UX',
    description: 'A parenting app designed to support families through the journey of raising children with intention.',
    color: '#1a1915',
    accent: '#8B7355',
    image: '/bunav.webp',
    front: '/projectthumbnail/bunav1.webp',
    back: '/projectthumbnail/bunav2.webp',
    url: '/work/bunav',
  },
  {
    id: 'carbon-count',
    name: 'Carbon Counts — Interactive Billing Experience',
    categoryLabel: 'Interaction Design',
    type: 'UX & Service Design',
    description: 'A retail technology concept that makes carbon footprint visible at the point of purchase.',
    color: '#131210',
    accent: '#6B5B4E',
    image: '/carbon-counts.webp',
    front: '/projectthumbnail/CarbonCounts1.webp',
    back: '/projectthumbnail/CarbonCounts2.webp',
    url: '/work/carbon-count',
  },
  {
    id: 'paradyes',
    name: 'Paradyes — Hair Color Experience Study',
    categoryLabel: 'UX Case Study',
    type: 'Branding & Visual System',
    description: 'The project is about design thinking, from research to actionable product and brand strategy.',
    color: '#111009',
    accent: '#7A6B5A',
    image: '/paradyes.webp',
    front: '/projectthumbnail/Paradyes1.webp',
    back: '/projectthumbnail/Paradyes2.webp',
    url: '/work/paradyes',
  },
  {
    id: 'ember',
    name: 'Ember — Mental Wellness App',
    categoryLabel: 'Brand Identity & UX',
    type: 'App Design & UX',
    description: 'A mental health app built around warmth and continuity — a gentle space for daily emotional check-ins.',
    color: '#141310',
    accent: '#5C5248',
    image: '/ember.webp',
    front: '/projectthumbnail/Ember1.webp',
    back: '/projectthumbnail/Ember2.webp',
    url: '/work/ember',
  },
  {
    id: 'ferrari-ar',
    name: 'Red Found Its Form — AR Experience',
    categoryLabel: 'AR Experience Design',
    type: 'Interaction Design',
    description: 'An augmented reality experience that brings the Ferrari brand to life through immersive spatial design.',
    color: '#110a08',
    accent: '#7A3020',
    image: '/ferrari-ar.webp',
    front: '/projectthumbnail/Ferrari1.webp',
    back: '/projectthumbnail/Ferrari2.webp',
    url: '/work/ferrari-ar',
  },
  {
    id: 'anvaya',
    name: 'Anvaya — Personalized Skincare Kiosk',
    categoryLabel: 'CX Research and Service Design',
    type: 'Branding & Visual System',
    description: 'Anvaya is a personalized skincare experience that reimagines how people understand and care for their skin.',
    color: '#0e0e0c',
    accent: '#5C6B4E',
    image: '/anvaya.webp',
    front: '/projectthumbnail/Anvaya1.webp',
    back: '/projectthumbnail/Anvaya2.webp',
    url: '/work/anvaya',
  },
];

const ARCHIVE = [
  {
    id: 1,
    title: 'Nidra App',
    subtitle: 'AI dream decoding & visual storytelling',
    tag: 'UI/UX, Speculative Design',
    readTime: '',
    image: '/archivethumbnails/nidra.webp',
    url: 'https://www.behance.net/gallery/243816955/Nidra-UIUX-Design',
  },
  {
    id: 2,
    title: 'Aadhaar Case Study',
    subtitle: 'Mapping migrant women\'s identity journeys',
    tag: 'Social Impact, Research Methodology',
    readTime: '',
    image: '/archivethumbnails/adhaar.webp',
    url: 'https://medium.com/@priyashreeacharya2002/barriers-to-identity-aadhaar-case-study-04af72112890',
  },
  {
    id: 3,
    title: 'Design for Engagement',
    subtitle: 'Behavioral design for dark store workers',
    tag: 'Behavioral Intervention, Systems Thinking',
    readTime: '',
    image: '/archivethumbnails/swiggy.webp',
    url: 'https://medium.com/@priyashreeacharya2002/designing-for-engagement-a-behavioral-intervention-for-blue-collar-workers-at-swiggy-a6a98383865f',
  },
  {
    id: 4,
    title: 'POSCA Brand Campaign',
    subtitle: 'A nostalgia-led campaign for POSCA markers',
    tag: 'Creative Direction, Campaign Design',
    readTime: '',
    image: '/archivethumbnails/posca.webp',
    url: 'https://www.behance.net/gallery/249558325/Posca-Markers-Brand-Campaign',
  },
  {
    id: 5,
    title: 'Toxic Masculinity',
    subtitle: 'A poster design addressing men\'s emotional struggles',
    tag: 'Concept Art, Visual Design',
    readTime: '',
    image: '/archivethumbnails/toxic-masculinity.webp',
    url: 'https://medium.com/@priyashreeacharya2002/melt-the-weight-of-expectations-visual-design-6464dff5f085',
  },
  {
    id: 6,
    title: 'Begumiyat: Restoring Women\'s Legacy',
    subtitle: 'Reimagining forgotten women rulers through portraiture',
    tag: 'Art Direction, Visual Storytelling',
    readTime: '',
    image: '/archivethumbnails/begumiyat.webp',
    url: 'https://www.behance.net/gallery/249557941/Begumiyat-Restoring-Womens-Legacy',
  },
];

const RING_CAROUSEL_CARDS = [
  { gradient: 'linear-gradient(130deg,#1a0608 0%,#6e1020 50%,#1a0608 100%)', image: '/createtogethr/1p.webp', darken: true },
  { gradient: 'linear-gradient(130deg,#050d18 0%,#0a2e6e 50%,#050d18 100%)', image: '/createtogethr/2.webp' },
  { gradient: 'linear-gradient(130deg,#0e0618 0%,#3a0e6e 50%,#0e0618 100%)', image: '/createtogethr/3.webp', darken: true },
  { gradient: 'linear-gradient(130deg,#041210 0%,#085e46 50%,#041210 100%)', image: '/createtogethr/4.webp' },
  { gradient: 'linear-gradient(130deg,#160c02 0%,#6e3c06 50%,#160c02 100%)', image: '/createtogethr/5.webp', darken: true },
  { gradient: 'linear-gradient(130deg,#140408 0%,#6e1048 50%,#140408 100%)', image: '/createtogethr/7.webp' },
  { gradient: 'linear-gradient(130deg,#080e04 0%,#2e5608 50%,#080e04 100%)', image: '/createtogethr/8.webp', darken: true },
  { gradient: 'linear-gradient(130deg,#060410 0%,#1a0860 50%,#060410 100%)', image: '/createtogethr/10.webp' },
  { gradient: 'linear-gradient(130deg,#100408 0%,#501028 50%,#100408 100%)', image: '/createtogethr/11.webp', darken: true },
  { gradient: 'linear-gradient(130deg,#0a0810 0%,#3a1858 50%,#0a0810 100%)', image: '/createtogethr/12.webp' },
];

const CABINET_SERVICES = [
  { label: 'Brand Identity', items: ['Logo & Visual Language', 'Brand Strategy', 'Messaging & Voice', 'Brand Guidelines'] },
  { label: 'Digital Design', items: ['Web & App Design', 'Design Systems', 'Prototyping', 'UX Research'] },
  { label: 'Motion & Film', items: ['Motion Graphics', 'Title Design', 'Short Film Identity', 'Visual Storytelling'] },
  { label: 'Print & Editorial', items: ['Publication Design', 'Poster & Collateral', 'Packaging', 'Wayfinding'] },
];

// ─── HOOKS ────────────────────────────────────────────────────────────────────

function useScrolled(threshold = 60) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

function useFadeIn() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
}

// ─── ANIMATION STYLE ──────────────────────────────────────────────────────────

function fadeUp(visible, delay = 0) {
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(32px)',
    transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
  };
}

// ─── NAV ─────────────────────────────────────────────────────────────────────

function LogoLink() {
  const navigate = useNavigate();
  return (
    <a
      href="/"
      style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', textDecoration: 'none', lineHeight: 1 }}
      onClick={e => {
        e.preventDefault();
        navigate('/');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      <img src="/P.LOGO.webp" alt="Priyashree" style={{ height: '42px', width: 'auto', display: 'block' }} />
    </a>
  );
}

export function Nav() {
  const scrolled = useScrolled();
  const [hoveredLink, setHoveredLink] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const total = document.documentElement.scrollHeight - window.innerHeight;
        setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); if (rafId) cancelAnimationFrame(rafId); };
  }, []);

  const navStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 40px',
    height: '64px',
    background: scrolled
      ? 'rgba(13, 12, 10, 0.85)'
      : 'transparent',
    backdropFilter: scrolled ? 'blur(16px)' : 'none',
    WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
    transition: 'background 0.4s ease, backdrop-filter 0.4s ease',
  };

  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const sectionMap = { work: 'WORK', digests: 'ARCHIVE', theory: 'THEORY' };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(sectionMap[entry.target.id] || null);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    ['work', 'digests', 'theory'].forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const navLinks = [
    { label: 'WORK', href: '#work' },
    { label: 'ARCHIVE', href: '#digests' },
    { label: 'THEORY', href: '#theory' },
    { label: 'ABOUT', href: '/about' },
  ];

  return (
    <nav style={navStyle}>
      <style>{`
        @keyframes meGlow {
          0%, 100% { text-shadow: 0 0 6px rgba(216,16,96,0.7), 0 0 16px rgba(216,16,96,0.4); }
          50%       { text-shadow: 0 0 14px rgba(216,16,96,1),   0 0 32px rgba(216,16,96,0.7); }
        }
      `}</style>
      {/* Rainbow scroll progress bar */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: `${scrollProgress}%`,
        height: '2px',
        background: 'linear-gradient(to right, #ff0000, #ff7700, #ffdd00, #00cc44, #0088ff, #8844ff)',
        transition: 'width 0.1s linear',
        pointerEvents: 'none',
      }} />
      <LogoLink />

      {/* Desktop nav links */}
      {!isMobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '48px' }}>
          {navLinks.map((link, i) => (
            <NavLink
              key={link.label}
              link={link}
              isHovered={hoveredLink === i}
              onHover={() => setHoveredLink(i)}
              onLeave={() => setHoveredLink(null)}
              isActive={activeSection === link.label}
            />
          ))}
        </div>
      )}

      {/* Mobile hamburger */}
      {isMobile && (
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            background: 'none',
            border: 'none',
            color: '#B0ADA6',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '8px',
            lineHeight: 1,
          }}
        >
          {menuOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Mobile menu overlay */}
      {isMobile && menuOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(13,12,10,0.97)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          zIndex: 99,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '48px',
        }}>
          {navLinks.map((link, i) => (
            <NavLink
              key={link.label}
              link={link}
              isHovered={hoveredLink === i}
              onHover={() => setHoveredLink(i)}
              onLeave={() => setHoveredLink(null)}
              onClick={() => setMenuOpen(false)}
            />
          ))}
        </div>
      )}
    </nav>
  );
}

function NavLink({ link, isHovered, onHover, onLeave, onClick, isActive }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isPageLink = !link.href.startsWith('#');
  const isOnHome = location.pathname === '/';

  return (
    <a
      href={link.href}
      style={{
        fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, Arial, sans-serif",
        fontWeight: 400,
        fontSize: '12.1px',
        letterSpacing: '0.08em',
        textDecoration: 'none',
        color: (isHovered || isActive || (isPageLink && location.pathname === link.href)) ? '#D81060' : '#B0ADA6',
        transition: 'color 0.3s ease',
        cursor: 'pointer',
      }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={e => {
        e.preventDefault();
        if (onClick) onClick();
        if (isPageLink) {
          navigate(link.href);
        } else if (isOnHome) {
          document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/');
          setTimeout(() => {
            document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        }
      }}
    >
      {link.label}
    </a>
  );
}

// ─── HERO ─────────────────────────────────────────────────────────────────────


function PrismBg() {
  const canvasRef = useRef(null);
  const threeRef  = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const threeCanvas = threeRef.current;

    let W = canvas.offsetWidth;
    let H = canvas.offsetHeight;
    canvas.width = W; canvas.height = H;
    let mx = W * 0.35, my = H * 0.42;
    let canvasRect = canvas.getBoundingClientRect();
    let animFrame;
    let renderer;
    let visObs;
    let cancelled = false;

    const onMove = e => { mx = e.clientX - canvasRect.left; my = e.clientY - canvasRect.top; };
    const onResize = () => {
      W = canvas.offsetWidth; H = canvas.offsetHeight;
      canvas.width = W; canvas.height = H;
      canvasRect = canvas.getBoundingClientRect();
    };
    const onScroll = () => { canvasRect = canvas.getBoundingClientRect(); };

    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });

    import('three').then(THREE => {
      if (cancelled) return;

    // ── Three.js pyramid ───────────────────────────────────────────────────
    renderer = new THREE.WebGLRenderer({ canvas: threeCanvas, alpha: true, antialias: true });
    renderer.setSize(224, 224);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const scene  = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.set(0, 0.1, 4);

    const geo  = new THREE.ConeGeometry(1, 1.6, 4, 1);
    geo.rotateY(Math.PI / 4);
    const mat  = new THREE.MeshPhysicalMaterial({
      color:              0xffffff,
      transmission:       0.92,
      roughness:          0,
      metalness:          0,
      ior:                2.4,
      thickness:          2,
      transparent:        true,
      opacity:            0.18,
      clearcoat:          1.0,
      clearcoatRoughness: 0.0,
      reflectivity:       1.0,
    });
    const mesh = new THREE.Mesh(geo, mat);
    scene.add(mesh);

    // Core edges — bright
    const edgesGeo = new THREE.EdgesGeometry(geo);
    const edgesMat = new THREE.LineBasicMaterial({ color: 0xddeeff, opacity: 0.92, transparent: true });
    const edges    = new THREE.LineSegments(edgesGeo, edgesMat);
    scene.add(edges);

    // Glow edge layer 1 — close halo, cyan tint
    const glowGeo1 = new THREE.EdgesGeometry(new THREE.ConeGeometry(1.014, 1.622, 4, 1));
    glowGeo1.rotateY(Math.PI / 4);
    const glowMat1 = new THREE.LineBasicMaterial({ color: 0x88ccff, opacity: 0.18, transparent: true });
    const glowEdges1 = new THREE.LineSegments(glowGeo1, glowMat1);
    scene.add(glowEdges1);

    // Glow edge layer 2 — wide soft bloom, blue-violet
    const glowGeo2 = new THREE.EdgesGeometry(new THREE.ConeGeometry(1.035, 1.656, 4, 1));
    glowGeo2.rotateY(Math.PI / 4);
    const glowMat2 = new THREE.LineBasicMaterial({ color: 0x6677ff, opacity: 0.07, transparent: true });
    const glowEdges2 = new THREE.LineSegments(glowGeo2, glowMat2);
    scene.add(glowEdges2);

    const R3 = Math.SQRT2 / 3;
    const CY = -0.8 / 3;
    const FACES = [
      { normal: new THREE.Vector3( 1, 0,  0), centroid: new THREE.Vector3( R3, CY,   0) },
      { normal: new THREE.Vector3( 0, 0,  1), centroid: new THREE.Vector3(  0, CY,  R3) },
      { normal: new THREE.Vector3(-1, 0,  0), centroid: new THREE.Vector3(-R3, CY,   0) },
      { normal: new THREE.Vector3( 0, 0, -1), centroid: new THREE.Vector3(  0, CY, -R3) },
    ];

    // Key light — strong specular on left face
    const dirLight = new THREE.DirectionalLight(0xffffff, 3.5);
    dirLight.position.set(-3, 1, 2);
    scene.add(dirLight);

    scene.add(new THREE.AmbientLight(0xffffff, 0.25));

    // Spot — top highlight
    const spotLight = new THREE.SpotLight(0xffffff, 4);
    spotLight.position.set(-3, 4, 3);
    spotLight.angle    = 0.3;
    spotLight.penumbra = 0.8;
    scene.add(spotLight);

    // Inner glow
    const innerLight = new THREE.PointLight(0xaaddff, 0.6);
    innerLight.position.set(0, 0, 0);
    scene.add(innerLight);

    // Specular fill — creates reflection hotspot on front-right face
    const specLight1 = new THREE.PointLight(0xffffff, 5, 6);
    specLight1.position.set(-1.2, 1.8, 1.6);
    scene.add(specLight1);

    // Cool rim light — blue-white reflection on back edge
    const specLight2 = new THREE.PointLight(0xaabbff, 3, 5);
    specLight2.position.set(1.8, 0.6, 1.2);
    scene.add(specLight2);

    // Warm under-bounce — subtle warm reflection from below
    const specLight3 = new THREE.PointLight(0xffeedd, 1.5, 4);
    specLight3.position.set(0, -1.5, 1.5);
    scene.add(specLight3);

    const fresnelGeo = new THREE.ConeGeometry(1.02, 1.632, 4, 1);
    fresnelGeo.rotateY(Math.PI / 4);
    const fresnelMat = new THREE.MeshBasicMaterial({
      color:       0xffffff,
      side:        THREE.BackSide,
      transparent: true,
      opacity:     0.08,
    });
    const fresnelMesh = new THREE.Mesh(fresnelGeo, fresnelMat);
    scene.add(fresnelMesh);

    let rayAlpha = 0;
    let isVisible = true;
    visObs = new IntersectionObserver(([e]) => { isVisible = e.isIntersecting; }, { threshold: 0 });
    visObs.observe(canvas);

    function seg(ax,ay,bx,by,cx,cy,dx,dy) {
      const d = (dy-cy)*(bx-ax)-(dx-cx)*(by-ay);
      if (Math.abs(d)<1e-8) return null;
      const t = ((dx-cx)*(ay-cy)-(dy-cy)*(ax-cx))/d;
      const u = ((bx-ax)*(ay-cy)-(by-ay)*(ax-cx))/d;
      if (t>=0&&t<=1&&u>=0&&u<=1) return {x:ax+t*(bx-ax),y:ay+t*(by-ay),t,u};
      return null;
    }

    function volumetricBeam(ox,oy,tx,ty) {
      if (!isFinite(ox)||!isFinite(oy)||!isFinite(tx)||!isFinite(ty)) return;
      const dx=tx-ox, dy=ty-oy;
      const angle=Math.atan2(dy,dx);
      const N=18;
      ctx.save();
      ctx.globalCompositeOperation='screen';
      const passes=[
        [ 18,  7,  0.18 ],  // outer glow
        [  6,  2,  0.35 ],  // bright core
      ];
      passes.forEach(([rl, rp, alpha]) => {
        for (let i=0; i<N; i++) {
          const t=i/(N-1);
          const bx=ox+dx*t, by=oy+dy*t;
          let s=1;
          if (i<20)    s=1+(19-i)*0.5/19;
          if (i>=N-20) s=1-((i-(N-20))/19)*0.6;
          s=Math.max(0.15,s);
          const erl=rl*s, erp=rp*s;
          ctx.save();
          ctx.translate(bx, by);
          ctx.rotate(angle);
          ctx.scale(1, erp/erl);
          const grd=ctx.createRadialGradient(0,0,0, 0,0,erl);
          grd.addColorStop(0, `rgba(255,255,255,${alpha})`);
          grd.addColorStop(1, 'rgba(255,255,255,0)');
          ctx.beginPath();
          ctx.arc(0, 0, erl, 0, Math.PI*2);
          ctx.fillStyle=grd;
          ctx.fill();
          ctx.restore();
        }
      });
      ctx.restore();
    }

    function drawSparkle(sx,sy) {
      const long=10, short=1.5;
      ctx.save();
      ctx.fillStyle='#ffffff';
      ctx.beginPath();
      for (let i=0;i<8;i++) {
        const a=i*Math.PI/4 - Math.PI/2;
        const r=i%2===0 ? long : short;
        const x=sx+Math.cos(a)*r, y=sy+Math.sin(a)*r;
        i===0 ? ctx.moveTo(x,y) : ctx.lineTo(x,y);
      }
      ctx.closePath(); ctx.fill();
      ctx.restore();
    }

    const RAYS = [
      [  7,   255,  30,   0],
      [  4.5, 255, 120,   0],
      [  2,   230, 255,   0],
      [  0,     0, 255,  60],
      [ -2,     0,  80, 255],
      [ -4.5,  60,   0, 200],
      [ -7,   140,   0, 255],
    ];

    function spectrumRay(ex, ey, angleDeg, r, g, b) {
      if (!isFinite(ex)||!isFinite(ey)) return;
      const a  = angleDeg * Math.PI / 180;
      const ca = Math.cos(a), sa = Math.sin(a);
      const px = -sa, py = ca;
      const reach = W - ex + 300;
      const fx = ex + ca*reach, fy = ey + sa*reach;
      const nearHW = 4, farHW = 48;
      const passes = [
        [0.10,  0.02],
        [0.40,  0.10],
      ];
      passes.forEach(([a0, a1]) => {
        const grd = ctx.createLinearGradient(ex, ey, fx, fy);
        grd.addColorStop(0, `rgba(${r},${g},${b},${a0})`);
        grd.addColorStop(1, `rgba(${r},${g},${b},${a1})`);
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.moveTo(ex + px*nearHW, ey + py*nearHW);
        ctx.lineTo(ex - px*nearHW, ey - py*nearHW);
        ctx.lineTo(fx - px*farHW,  fy - py*farHW);
        ctx.lineTo(fx + px*farHW,  fy + py*farHW);
        ctx.closePath();
        ctx.fillStyle = grd;
        ctx.fill();
        ctx.restore();
      });
    }

    let lastTs = 0;
    function tick(ts) {
      animFrame = requestAnimationFrame(tick);
      if (!isVisible || ts - lastTs < 33) return; // pause off-screen, cap to ~30 fps
      lastTs = ts;
      ctx.clearRect(0,0,W,H);

      const pcx = W * 0.63, pcy = H * 0.45;
      const PW = 120, PH = 100;
      const top = [pcx,        pcy - PH/2];
      const bl  = [pcx - PW/2, pcy + PH/2];
      const br  = [pcx + PW/2, pcy + PH/2];

      // Beam origin: left edge of TV screen, vertically centered
      const originX = pcx - 195, originY = pcy + 50;
      const dirX = mx - originX || 1;
      const dirY = my - originY;
      const mag  = Math.sqrt(dirX*dirX+dirY*dirY)||1;
      const far  = W*4;
      const bx2  = originX+(dirX/mag)*far;
      const by2  = originY+(dirY/mag)*far;

      const hitL = seg(originX,originY,bx2,by2, top[0],top[1],bl[0],bl[1]);
      const hitR = seg(originX,originY,bx2,by2, top[0],top[1],br[0],br[1]);
      const hitB = seg(originX,originY,bx2,by2, bl[0],bl[1],br[0],br[1]);

      const hits = [hitL,hitR,hitB].filter(Boolean).sort((a,b)=>a.t-b.t);
      const entry = hits[0]||null, exit = hits[1]||null;
      const isHit = hits.length >= 2;

      if (isHit) rayAlpha = Math.min(1, rayAlpha+0.055);
      else        rayAlpha = Math.max(0, rayAlpha-0.038);

      mesh.updateMatrixWorld();
      const mw = mesh.matrixWorld;
      let maxLeft = -Infinity, maxRight = -Infinity;
      let entryFace = FACES[2], exitFace = FACES[0];
      FACES.forEach(f => {
        const wn = f.normal.clone().transformDirection(mw);
        const dotL = -wn.x;
        const dotR =  wn.x;
        if (dotL > maxLeft)  { maxLeft  = dotL; entryFace = f; }
        if (dotR > maxRight) { maxRight = dotR; exitFace  = f; }
      });

      const _ec = entryFace.centroid.clone().applyMatrix4(mw);
      _ec.project(camera);
      const entryPx = { x: W * 0.63 + _ec.x * 160, y: H * 0.45 - _ec.y * 160 };

      const _xc = exitFace.centroid.clone().applyMatrix4(mw);
      _xc.project(camera);
      const exitPx  = { x: W * 0.63 + _xc.x * 160, y: H * 0.45 - _xc.y * 160 };

      const beamEndX = isHit ? entryPx.x : Math.min(mx, top[0]-10);
      const beamEndY = isHit ? entryPx.y : my;
      volumetricBeam(originX, originY, beamEndX, beamEndY);

      mesh.rotation.y        += 0.004;
      edges.rotation.y       += 0.004;
      glowEdges1.rotation.y  += 0.004;
      glowEdges2.rotation.y  += 0.004;
      fresnelMesh.rotation.y += 0.004;
      renderer.render(scene, camera);

      if (rayAlpha > 0) {
        const ex = exitPx.x, ey = exitPx.y;
        ctx.globalAlpha = rayAlpha;
        RAYS.forEach(([deg,r,g,b]) => spectrumRay(ex, ey, deg, r, g, b));

        if (isFinite(ex) && isFinite(ey)) {
          const topA = -7 * Math.PI/180, botA = 7 * Math.PI/180;
          const reach = W - ex + 300;
          const fxT = ex + Math.cos(topA)*reach, fyT = ey + Math.sin(topA)*reach;
          const fxB = ex + Math.cos(botA)*reach, fyB = ey + Math.sin(botA)*reach;
          const hslGrd = ctx.createLinearGradient(fxT, fyT, fxB, fyB);
          hslGrd.addColorStop(0,    'hsla(270,100%,55%,0.15)');
          hslGrd.addColorStop(0.17, 'hsla(240,100%,55%,0.15)');
          hslGrd.addColorStop(0.33, 'hsla(210,100%,55%,0.15)');
          hslGrd.addColorStop(0.5,  'hsla(120,100%,50%,0.15)');
          hslGrd.addColorStop(0.67, 'hsla(60,100%,50%,0.15)');
          hslGrd.addColorStop(0.83, 'hsla(30,100%,50%,0.15)');
          hslGrd.addColorStop(1,    'hsla(0,100%,50%,0.15)');
          ctx.save();
          ctx.globalCompositeOperation = 'screen';
          ctx.beginPath();
          ctx.moveTo(ex, ey - 3); ctx.lineTo(ex, ey + 3);
          ctx.lineTo(fxB, fyB);   ctx.lineTo(fxT, fyT);
          ctx.closePath();
          ctx.fillStyle = hslGrd; ctx.fill();
          ctx.restore();
        }

        ctx.globalAlpha = 1;
        ctx.save();
        ctx.beginPath(); ctx.arc(ex,ey,3.5,0,Math.PI*2);
        ctx.fillStyle='rgba(255,255,255,0.95)';
        ctx.fill();
        ctx.restore();
      }

      if (isHit) drawSparkle(entryPx.x, entryPx.y);
    }

    animFrame = requestAnimationFrame(tick);
    }); // end import('three').then

    return () => {
      cancelled = true;
      cancelAnimationFrame(animFrame);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
      if (visObs) visObs.disconnect();
      if (renderer) renderer.dispose();
    };
  }, []);

  return (
    <div style={{position:'absolute',inset:0,pointerEvents:'none',zIndex:0,
      display: window.innerWidth < 768 ? 'none' : 'block'}}>
      <canvas ref={canvasRef} style={{position:'absolute',inset:0,width:'100%',height:'100%',filter:'blur(4px)',willChange:'transform'}}/>
      <canvas ref={threeRef} width={224} height={224} style={{
        position:'absolute',
        left:'63%', top:'45%',
        transform:'translate(-50%,-50%)',
        pointerEvents:'none',
      }}/>
    </div>
  );
}

const LOGO_ITEMS = [
  { src: '/icons/aadhaar.webp', alt: 'Aadhaar' },
  { src: '/icons/idf.webp', alt: 'IDF' },
  { src: '/icons/ignca.webp', alt: 'IGNCA' },
  { src: '/icons/mlslogoo@3x.webp', alt: 'MLS' },
  { src: '/icons/nid.webp', alt: 'NID' },
  { src: '/icons/sacac.webp', alt: 'SACAC' },
  { src: '/icons/sap.webp', alt: 'SAP' },
  { src: '/icons/ss4.webp', alt: 'SS4' },
  { src: '/icons/tbt.webp', alt: 'The Banyan Tee' },
];

function VideoHero() {
  return (
    <section style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden' }}>
      <style>{`
        @font-face {
          font-family: 'Aujournuit';
          src: url('/fonts/Aujournuit-Densed.woff2') format('woff2'),
               url('/fonts/Aujournuit-Densed.woff') format('woff'),
               url('/fonts/Aujournuit-Densed.otf') format('opentype'),
               url('/fonts/Aujournuit-Densed.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        @font-face {
          font-family: 'Aujournuit Condensed';
          src: url('/fonts/Aujournuit-Condensed.otf') format('opentype'),
               url('/fonts/Aujournuit-Condensed.ttf') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
        @keyframes vhScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .vh-logo-track {
          display: flex;
          align-items: center;
          width: max-content;
          animation: vhScroll 22s linear infinite;
          will-change: transform;
        }
        .vh-logo-strip-outer {
          -webkit-mask-image: linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%);
          mask-image: linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%);
        }
        @keyframes vhGrainShift {
          0%   { transform: translate(0, 0); }
          20%  { transform: translate(-3px, 2px); }
          40%  { transform: translate(2px, -3px); }
          60%  { transform: translate(-1px, 3px); }
          80%  { transform: translate(3px, -1px); }
          100% { transform: translate(0, 0); }
        }
        .vh-grain {
          animation: vhGrainShift 0.45s steps(3) infinite;
          will-change: transform;
        }
      `}</style>

      {/* Video background */}
      <video
        autoPlay muted loop playsInline preload="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
        src="/backgroundforherosection.webm"
      />

      {/* Dark overlay */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.62)', zIndex: 1 }} />

      {/* Grain/noise overlay */}
      <div
        className="vh-grain"
        style={{
          position: 'absolute', inset: '-50%',
          width: '200%', height: '200%',
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.95' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='saturate' values='0'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundRepeat: 'repeat',
          backgroundSize: '150px 150px',
          opacity: 0.18,
          zIndex: 2,
          pointerEvents: 'none',
          mixBlendMode: 'overlay',
          willChange: 'transform',
        }}
      />

      {/* Main centered content */}
      <div style={{
        position: 'relative', zIndex: 3,
        height: 'calc(100% - 120px)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', padding: '15vh 24px 0',
        fontFamily: "'Aujournuit', Garamond, 'EB Garamond', Georgia, serif",
      }}>

        {/* Pre-headline */}
        <p style={{
          fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif", fontWeight: 400,
          fontSize: 'clamp(14px,1.5vw,18px)',
          color: 'rgba(255,255,255,0.68)', lineHeight: 1.7,
          maxWidth: '520px', margin: '0 0 16px',
        }}>
          Hey! I am Priyashree
        </p>

        {/* Headline */}
        <h1 style={{
          fontFamily: "'Aujournuit', Garamond, Georgia, serif",
          fontWeight: 400,
          fontSize: 'clamp(38px,7.5vw,100px)',
          color: '#fff', lineHeight: 1.08,
          margin: '0 0 24px', maxWidth: '900px',
          letterSpacing: '-0.02em',
          textShadow: '0 0 30px rgba(255,255,255,0.18), 0 0 80px rgba(255,255,255,0.08), 0 0 140px rgba(200,200,255,0.06)',
          WebkitTextStroke: 'none',
        }}>
          Designing for the way people think, feel and connect.
        </h1>

        {/* Subtitle */}
        <p style={{
          fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif", fontWeight: 400,
          fontSize: 'clamp(14px,1.5vw,18px)',
          color: 'rgba(255,255,255,0.68)', lineHeight: 1.7,
          maxWidth: '520px', margin: '0 0 72px',
        }}>
          Psychologist | Designer | Strategist
        </p>

        {/* Logo slider — inline, right below subtitle */}
        <p style={{ fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif", fontSize: '10px', fontWeight: 400, letterSpacing: '0.18em', color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: '32px 0 32px', textTransform: 'uppercase' }}>
          Collaborations
        </p>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <div
            className="vh-logo-strip-outer"
            style={{ width: '48%', overflow: 'hidden', display: 'flex', alignItems: 'center' }}
          >
            <div className="vh-logo-track">
              {[...LOGO_ITEMS, ...LOGO_ITEMS].map((logo, i) => (
                <div key={i} style={{ padding: '0 48px', flexShrink: 0 }}>
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    style={{
                      height: '32px',
                      width: 'auto',
                      objectFit: 'contain',
                      opacity: 0.65,
                      display: 'block',
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* Bottom blend gradient into Selected Works background */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '280px',
        background: 'linear-gradient(to bottom, transparent 0%, #0F0E0C 100%)',
        zIndex: 2,
        pointerEvents: 'none',
      }} />
    </section>
  );
}

function Hero() {
  const [prismHovered, setPrismHovered] = useState(false);
  const heroRef = useRef(null);
  const slotRef = useRef(null);
  const wordInnerRef = useRef(null);
  const currentIdxRef = useRef(0);

  function measureWord(text) {
    const ghost = document.createElement('span');
    const fs = slotRef.current
      ? getComputedStyle(slotRef.current.parentElement).fontSize
      : '52px';
    ghost.style.cssText = `position:absolute;visibility:hidden;white-space:nowrap;font-family:'Aujournuit',Georgia,serif;font-style:italic;font-size:${fs};font-weight:300;letter-spacing:-0.01em`;
    ghost.textContent = text;
    document.body.appendChild(ghost);
    const w = ghost.offsetWidth;
    ghost.remove();
    return w;
  }

  useEffect(() => {
    if (slotRef.current) {
      slotRef.current.style.width = measureWord(ROTATING_WORDS[0]) + 'px';
    }
    const interval = setInterval(() => {
      const next = (currentIdxRef.current + 1) % ROTATING_WORDS.length;
      if (wordInnerRef.current) wordInnerRef.current.className = 'word-slot-inner exit';
      setTimeout(() => {
        if (slotRef.current) slotRef.current.style.width = measureWord(ROTATING_WORDS[next]) + 'px';
        if (wordInnerRef.current) {
          wordInnerRef.current.textContent = ROTATING_WORDS[next];
          wordInnerRef.current.className = 'word-slot-inner enter';
        }
        currentIdxRef.current = next;
      }, 320);
    }, 2600);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proximity-based hover: check if mouse is within ~180px of prism centre
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    let heroRect = hero.getBoundingClientRect();
    const onResize = () => { heroRect = hero.getBoundingClientRect(); };
    const onScroll = () => { heroRect = hero.getBoundingClientRect(); };
    window.addEventListener('resize', onResize, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    const onMove = (e) => {
      const prismCx = heroRect.left + heroRect.width  * 0.63;
      const prismCy = heroRect.top  + heroRect.height * 0.45;
      const dx = e.clientX - prismCx;
      const dy = e.clientY - prismCy;
      setPrismHovered(Math.sqrt(dx * dx + dy * dy) < 180);
    };
    const onLeave = () => setPrismHovered(false);
    hero.addEventListener('mousemove', onMove, { passive: true });
    hero.addEventListener('mouseleave', onLeave, { passive: true });
    return () => {
      hero.removeEventListener('mousemove', onMove);
      hero.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      style={{
        minHeight: '100vh',
        background: COLORS.bg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '80px 60px 40px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background image — resized to fit exactly inside TV screen (no clip-path) */}
      {/* TV: 850px wide, right:60px, top:30px → screen: right:340px, top:259px, 392×289px */}
      <div style={{
        position: 'absolute',
        right: '340px',
        top: '259px',
        width: '392px',
        height: '289px',
        zIndex: 1,
        overflow: 'hidden',
      }}>
        <img
          src="/serene-floral-staircase.webp"
          alt=""
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'center',
            filter: prismHovered ? 'grayscale(0%)' : 'grayscale(100%)',
            opacity: 0.9,
            transition: 'filter 1.2s ease',
          }}
        />
      </div>

      {/* Prism + rainbow beam — clipped to TV screen bounds */}
      <div style={{
        position: 'absolute', inset: 0,
        pointerEvents: 'none', zIndex: 2,
        clipPath: 'polygon(calc(100% - 732px) 259px, calc(100% - 340px) 259px, calc(100% - 340px) 548px, calc(100% - 732px) 548px)',
      }}>
        <PrismBg />
      </div>

      {/* Vintage TV — 850px, full PNG visible (body + knobs + legs) */}
      <div style={{
        position: 'absolute',
        right: '60px',
        top: '30px',
        width: '850px',
        pointerEvents: 'none',
        zIndex: 3,
      }}>
        <img src="/tv.webp" alt="" style={{ width: '100%', display: 'block' }} />
      </div>

      {/* Hover hint — below the prism inside TV screen */}
      <div style={{
        position: 'absolute',
        right: '440px',
        top: '505px',
        zIndex: 4,
        pointerEvents: 'none',
        fontFamily: "'Neue Helvetica World', Helvetica, Arial, sans-serif",
        fontSize: '10px',
        letterSpacing: '0.2em',
        color: prismHovered ? 'rgba(255,255,255,0)' : 'rgba(255,255,255,0.25)',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        transition: 'color 0.8s ease',
        userSelect: 'none',
      }}>
        Hover here
      </div>

      {/* Atmospheric light rays */}
      <LightRays />

      {/* Coordinates */}


      {/* Main hero text — left side, beside the TV */}
      <div style={{ position: 'relative', zIndex: 4, maxWidth: '460px' }}>

        {/* Heading row — text left, button top-right */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: '32px',
          marginBottom: '48px',
        }}>
          <div style={{
            fontFamily: "'Aujournuit', Georgia, serif",
            fontWeight: 300,
            lineHeight: 1.08,
            fontSize: 'clamp(40px, 6.5vw, 88px)',
            color: COLORS.textPrimary,
            letterSpacing: '-0.03em',
          }}>
            It starts with a spark,<br />
            and becomes{' '}
            {/* Iridescent word slot */}
            <span
              ref={slotRef}
              style={{
                display: 'inline-block',
                position: 'relative',
                verticalAlign: 'bottom',
                minWidth: '10px',
                transition: 'width 0.45s cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              <span
                ref={wordInnerRef}
                className="word-slot-inner enter"
              >
                {ROTATING_WORDS[0]}
              </span>
            </span>
          </div>

        </div>

        {/* Tagline row */}
        <div style={{
          paddingTop: '16px',
        }}>

        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .word-slot-inner {
          display: inline-block;
          white-space: nowrap;
          font-family: 'Aujournuit', Georgia, serif;
          font-style: italic;
          font-weight: 300;
          letter-spacing: -0.01em;
          color: #ffffff;
          text-shadow: 0 0 18px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.15), 0 0 80px rgba(210,225,255,0.08), 0 0 140px rgba(200,215,255,0.05);
          will-change: transform, opacity;
        }
        .word-slot-inner.enter {
          animation: wordIn 0.5s cubic-bezier(0.16,1,0.3,1) forwards;
        }
        .word-slot-inner.exit {
          animation: wordOut 0.32s cubic-bezier(0.4,0,1,1) forwards;
        }
        @keyframes wordIn {
          from { opacity: 0; transform: translateY(60%) skewY(4deg); }
          to   { opacity: 1; transform: translateY(0) skewY(0deg); }
        }
        @keyframes wordOut {
          from { opacity: 1; transform: translateY(0) skewY(0deg); }
          to   { opacity: 0; transform: translateY(-50%) skewY(-3deg); }
        }
        @keyframes shimmer {
          0%   { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        @media (max-width: 768px) { .adam-ascii-bg { display: none !important; } }
      `}</style>
    </section>
  );
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────

function SectionLabel({ children, color }) {
  return (
    <div style={{
      fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
      fontWeight: 400,
      fontSize: '10px',
      letterSpacing: '0.12em',
      color: color || COLORS.textSecondary,
      marginBottom: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
    }}>
      <span style={{ color: COLORS.accent, fontWeight: 400 }}>/</span>
      {children}
    </div>
  );
}

function RainbowGlowButton({ href, children, target, rel }) {
  const btnRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const [hue, setHue] = useState(260);

  const handleMove = (e) => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    setMouse({ x, y });
    // hue shifts as cursor moves left→right: cyan(180) → violet(280) → pink(320)
    setHue(Math.round((x / r.width) * 200 + 160) % 360);
  };

  const h2 = (hue + 55) % 360;
  const h3 = (hue + 110) % 360;

  // border spotlight: radial bloom at cursor, fades to nothing — shown in the 1.5px padding gap
  const borderGlow = hovered
    ? `radial-gradient(90px circle at ${mouse.x}px ${mouse.y}px,
        hsl(${hue},100%,72%),
        hsl(${h2},100%,62%) 40%,
        hsl(${h3},85%,52%) 68%,
        transparent 100%)`
    : 'rgba(255,255,255,0.06)';

  // soft white ambient glow behind the button — minimal
  const ambientGlow = hovered
    ? `0 0 18px 2px rgba(255,255,255,0.07),
       0 6px 20px rgba(0,0,0,0.5)`
    : '0 4px 16px rgba(0,0,0,0.35)';

  return (
    <a
      ref={btnRef}
      href={href}
      target={target}
      rel={rel}
      onMouseMove={handleMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        padding: '1px',             // this gap IS the visible border ring
        borderRadius: '9999px',
        overflow: 'hidden',
        cursor: 'pointer',
        textDecoration: 'none',
        background: borderGlow,     // outer background shows through in the 1.5px ring
        transform: hovered ? 'scale(1.03)' : 'scale(1)',
        transition: 'transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.35s ease',
        boxShadow: ambientGlow,
      }}
    >
      {/* inner dark pill — covers center, leaving only border ring visible */}
      <div style={{
        background: '#0E0C0A',
        borderRadius: '9999px',
        padding: '12px 26px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <span style={{
          fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          fontSize: '15px',
          fontWeight: 400,
          color: '#EDEAE3',
          whiteSpace: 'nowrap',
          letterSpacing: '0.03em',
        }}>
          {children}
        </span>
      </div>
    </a>
  );
}


function CTAButton({ href, children, filled = false, wide = false }) {
  return (
    <a
      href={href}
      className={filled ? 'iridescent-btn' : wide ? 'frosted-btn-wide' : 'frosted-btn'}
      onClick={e => {
        if (href.startsWith('#')) {
          e.preventDefault();
          document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
        }
      }}
    >
      {children}
    </a>
  );
}

function Divider() {
  return <div style={{ borderTop: `1px solid ${COLORS.border}`, width: '100%' }} />;
}

// White bloom glow word — same effect as "her" in About section
function GlowWord({ children }) {
  return (
    <em style={{
      fontStyle: 'italic',
      fontWeight: 'inherit',
      color: '#ffffff',
      textShadow: '0 0 18px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.15), 0 0 80px rgba(210,225,255,0.08), 0 0 140px rgba(200,215,255,0.05)',
    }}>
      {children}
    </em>
  );
}

function IridescentWord({ children }) {
  return (
    <span style={{
      background: 'linear-gradient(90deg, #5dcaa5, #a78bfa, #7dd3fc, #f0abfc, #5dcaa5)',
      backgroundSize: '300% 100%',
      WebkitBackgroundClip: 'text',
      backgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      color: 'transparent',
      fontStyle: 'italic',
      animation: 'shimmer 4s linear infinite',
      display: 'inline',
    }}>
      {children}
    </span>
  );
}

// Subtle light ray overlay for atmosphere
function LightRays() {
  const rays = [
    { left: '36%', top: '0',   rotate: '-9deg',  h: '55%', w: '90px',  op: 0.032 },
    { left: '54%', top: '8%',  rotate: '13deg',  h: '40%', w: '60px',  op: 0.022 },
    { left: '66%', top: '2%',  rotate: '-4deg',  h: '48%', w: '40px',  op: 0.018 },
  ];
  return (
    <>
      {rays.map((r, i) => (
        <div key={i} style={{
          position: 'absolute',
          left: r.left, top: r.top,
          width: r.w, height: r.h,
          background: `linear-gradient(to bottom, rgba(255,255,255,${r.op}), transparent)`,
          transform: `rotate(${r.rotate}) translateX(-50%)`,
          pointerEvents: 'none',
          zIndex: 0,
          filter: 'blur(6px)',
          animation: `rayFade ${4 + i * 1.5}s ease-in-out infinite`,
        }} />
      ))}
    </>
  );
}

// ─── WORK / SHOWCASE ─────────────────────────────────────────────────────────

function WorkSection() {
  const [ref, visible] = useFadeIn();

  return (
    <section
      id="work"
      ref={ref}
      style={{
        background: '#f5f2ec',
        padding: '120px 40px',
        borderTop: '1px solid rgba(0,0,0,0.08)',
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '80px' }}>
          <div style={fadeUp(visible, 0)}>
            <SectionLabel color="rgba(0,0,0,0.4)">SELECTED WORKS</SectionLabel>
            <h2 style={{
              fontFamily: "'Aujournuit', Georgia, serif",
              fontWeight: 400,
              fontSize: 'clamp(36px, 5vw, 64px)',
              color: '#0E0C0A',
              lineHeight: 1.12,
              margin: 0,
              letterSpacing: '-0.01em',
            }}>
              Design for Purpose, <em style={{ fontStyle: 'italic', color: COLORS.accent }}>Emotions</em> and Solutions
            </h2>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '32px',
          maxWidth: '960px',
          margin: '0 auto',
        }}>
          {PROJECTS.map((project, i) => (
            <ProjectCard key={project.id} project={project} delay={i * 0.08} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ project, delay, visible }) {
  const [flipped, setFlipped] = useState(false);
  const navigate = useNavigate();
  const isInternal = project.url.startsWith('/');

  return (
    <a
      href={project.url}
      target={isInternal ? '_self' : '_blank'}
      rel={isInternal ? undefined : 'noopener noreferrer'}
      onClick={isInternal ? (e) => { e.preventDefault(); navigate(project.url); } : undefined}
      className="cursor-pulse"
      style={{
        display: 'block',
        textDecoration: 'none',
        perspective: '1200px',
        borderRadius: '12px',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
      }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      {/* Flip container */}
      <div style={{
        position: 'relative',
        width: '100%',
        transformStyle: 'preserve-3d',
        transition: 'transform 0.7s cubic-bezier(0.4, 0.2, 0.2, 1)',
        transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        borderRadius: '12px',
      }}>
        {/* Front face */}
        <div style={{
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'block',
        }}>
          <img
            src={project.front}
            alt={project.name}
            loading="lazy"
            style={{ width: '100%', display: 'block', borderRadius: '12px' }}
          />
        </div>

        {/* Back face */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          transform: 'rotateY(180deg)',
          borderRadius: '12px',
          overflow: 'hidden',
        }}>
          <img
            src={project.back}
            alt={project.name + ' back'}
            loading="lazy"
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '12px' }}
          />
        </div>
      </div>
    </a>
  );
}

// ─── ARCHIVE ─────────────────────────────────────────────────────────────────

function DigestsSection() {
  const [ref, visible] = useFadeIn();

  return (
    <section
      id="digests"
      ref={ref}
      style={{
        background: COLORS.bg,
        padding: '120px 40px 40px',
        borderTop: `1px solid ${COLORS.border}`,
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '80px', gap: '20px' }}>
          <div style={fadeUp(visible, 0)}>
            <SectionLabel>ARCHIVE</SectionLabel>
            <h2 style={{
              fontFamily: "'Aujournuit', Georgia, serif",
              fontWeight: 400,
              fontSize: 'clamp(36px, 5vw, 64px)',
              color: COLORS.textPrimary,
              lineHeight: 1.12,
              margin: 0,
              letterSpacing: '-0.01em',
            }}>
              Some <GlowWord>experiments</GlowWord> and explorations
            </h2>
          </div>
        </div>

        <MarqueeDigests visible={visible} />
      </div>
    </section>
  );
}

function MarqueeDigests({ visible }) {
  const [paused, setPaused] = useState(false);
  const items = [...ARCHIVE, ...ARCHIVE]; // duplicate for seamless loop

  return (
    <div style={{ overflow: 'hidden', width: '100%', padding: '0 0 40px' }}>
      <style>{`
        @keyframes marqueeScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .marquee-track {
          display: flex;
          gap: 24px;
          width: max-content;
          animation: marqueeScroll 22s linear infinite;
        }
        .marquee-track.paused {
          animation-play-state: paused;
        }
      `}</style>
      <div
        className={`marquee-track${paused ? ' paused' : ''}`}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {items.map((article, i) => (
          <div key={i} style={{ width: '420px', flexShrink: 0 }}>
            <DigestCard article={article} delay={0} visible={visible} />
          </div>
        ))}
      </div>
    </div>
  );
}

function DigestCard({ article, delay, visible }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        cursor: 'pointer',
        background: '#111',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.8s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => article.url && window.open(article.url, '_blank')}
    >
      {/* Image with inset padding */}
      <div style={{ padding: '16px 16px 0', background: '#111' }}>
        <div style={{
          width: '100%',
          height: '260px',
          overflow: 'hidden',
          borderRadius: '8px',
          background: '#111',
        }}>
          {article.image && (
            <img src={article.image} alt={article.title} loading="lazy" style={{
              width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center',
              transform: hovered ? 'scale(1.04)' : 'scale(1)',
              transition: 'transform 0.5s ease',
              display: 'block',
            }} />
          )}
        </div>
      </div>

      {/* Text area */}
      <div style={{ padding: '24px 20px 24px' }}>
        {/* Title */}
        <h3 style={{
          fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
          fontWeight: 400,
          fontSize: '22px',
          letterSpacing: '-0.01em',
          color: hovered ? COLORS.accent : COLORS.textPrimary,
          margin: '0 0 10px',
          lineHeight: 1.2,
          transition: 'color 0.3s ease',
        }}>
          {article.title}
        </h3>

        {/* Subtitle / one-liner */}
        <p style={{
          fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
          fontWeight: 400,
          fontSize: '11px',
          letterSpacing: '0.02em',
          color: COLORS.textSecondary,
          margin: '0 0 18px',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}>
          {article.subtitle}
        </p>

        {/* Tag pills */}
        {article.tag && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {article.tag.split(',').map((t, i) => (
              <span key={i} style={{
                display: 'inline-block',
                fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
                fontSize: '11px',
                letterSpacing: '0.04em',
                color: COLORS.textSecondary,
                border: `1px solid ${COLORS.border}`,
                borderRadius: '4px',
                padding: '6px 14px',
              }}>
                {t.trim()}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ABOUT ───────────────────────────────────────────────────────────────────

function SkillCircle({ label }) {
  const [hovered, setHovered] = useState(false);
  const size = 160;
  const r = (size / 2) - 1;
  const circumference = 2 * Math.PI * r;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {/* Static base border */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1"
        />
      </svg>

      {/* Animated trim-path stroke with glow */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: hovered ? 1 : 0, transition: 'opacity 0.15s ease' }}>
        <defs>
          <filter id="circle-glow">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="rgba(255,255,255,0.7)"
          strokeWidth="1"
          filter="url(#circle-glow)"
          strokeDasharray={circumference}
          strokeDashoffset={hovered ? 0 : circumference}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: hovered ? 'stroke-dashoffset 0.55s ease' : 'none' }}
        />
      </svg>

      {/* Text */}
      <span style={{
        fontFamily: "'Aujournuit', Georgia, serif",
        fontWeight: 300,
        fontSize: '15px',
        color: '#ffffff',
        textAlign: 'center',
        lineHeight: 1.5,
        whiteSpace: 'pre-line',
        padding: '0 12px',
        position: 'relative',
        zIndex: 1,
        transition: 'text-shadow 0.3s ease',
        textShadow: hovered
          ? '0 0 12px rgba(255,255,255,0.9), 0 0 24px rgba(255,255,255,0.4)'
          : 'none',
      }}>
        {label}
      </span>
    </div>
  );
}

const TOOLS = ['Figma', 'Framer', 'React', 'After Effects', 'Premiere', 'Illustrator', 'Photoshop', 'Miro', 'Notion', 'Webflow'];

// 4-column × 3-band layout matching reference screenshots. Grid: 1300px.
// Band 1 (y≈10–50): A5-1, A5-27, A5-5, A5-4
// Band 2 (y≈332–400): A5-7, A5-3, A5-28, A5-9
// Band 3 (y≈662–735): A5-30, A5-31, A5-26, A5-18
const BEYOND_PHOTOS = [
  // Band 1 — staggered top row
  { file: 'A5 - 1.webp',  rot:  0,  x: 20,  y: 50  },
  { file: 'A5 - 27.webp', rot: -2,  x: 303, y: 10  },
  { file: 'A5 - 5.webp',  rot: -3,  x: 545, y: 28  },
  { file: 'A5 - 4.webp',  rot:  0,  x: 840, y: 45  },
  // Band 2 — staggered middle row
  { file: 'A5 - 7.webp',  rot: -4,  x: 90,  y: 400 },
  { file: 'A5 - 3.webp',  rot:  3,  x: 317, y: 332 },
  { file: 'A5 - 28.webp', rot:  0,  x: 570, y: 377 },
  { file: 'A5 - 9.webp',  rot:  2,  x: 840, y: 334 },
  // Band 3 — staggered bottom row
  { file: 'A5 - 30.webp', rot: -2,  x: 70,  y: 732 },
  { file: 'A5 - 31.webp', rot:  0,  x: 320, y: 722 },
  { file: 'A5 - 26.webp', rot:  3,  x: 567, y: 710 },
  { file: 'A5 - 18.webp', rot:  2,  x: 840, y: 662 },
];

function BeyondDesignSection() {
  const dragging = useRef(null);
  const maxZ = useRef(BEYOND_PHOTOS.length);
  const imgRefs = useRef([]);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [imgs, setImgs] = useState(() =>
    BEYOND_PHOTOS.map((p, i) => ({ ...p, z: i + 1 }))
  );

  useEffect(() => {
    const onMove = e => {
      if (!dragging.current) return;
      const { idx, ox, oy, ix, iy } = dragging.current;
      const nx = ix + e.clientX - ox;
      const ny = iy + e.clientY - oy;
      dragging.current.cx = nx;
      dragging.current.cy = ny;
      const el = imgRefs.current[idx];
      if (el) { el.style.left = nx + 'px'; el.style.top = ny + 'px'; }
    };
    const onUp = () => {
      if (dragging.current) {
        const { idx, cx, cy } = dragging.current;
        if (cx !== undefined) {
          setImgs(prev => prev.map((img, i) => i === idx ? { ...img, x: cx, y: cy } : img));
        }
      }
      dragging.current = null;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = (e, idx, img) => {
    e.preventDefault();
    maxZ.current += 1;
    const z = maxZ.current;
    setImgs(prev => prev.map((p, i) => i === idx ? { ...p, z } : p));
    dragging.current = { idx, ox: e.clientX, oy: e.clientY, ix: img.x, iy: img.y };
  };

  return (
    <div style={{ marginTop: '80px' }}>
      {/* Heading above the grid */}
      <h2 style={{
        fontFamily: "'Aujournuit', Georgia, serif",
        fontSize: 'clamp(36px, 5vw, 68px)',
        fontWeight: 400,
        color: COLORS.textPrimary,
        margin: '0 0 40px 0',
        letterSpacing: '-0.02em',
        lineHeight: 1.15,
        textAlign: 'center',
      }}>
        Who am I <em style={{ textShadow: '0 0 18px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.15), 0 0 80px rgba(210,225,255,0.08), 0 0 140px rgba(200,215,255,0.05)' }}>beyond design?</em>
      </h2>

    <div style={{ position: 'relative', height: '1300px', overflow: 'hidden', borderRadius: '16px', userSelect: 'none' }}>
      <style>{`
        .beyond-grid {
          position: absolute; inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 24px 24px, 24px 24px, 120px 120px, 120px 120px;
        }
        .beyond-crosshairs {
          position: absolute; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='120' height='120' xmlns='http://www.w3.org/2000/svg'%3E%3Cline x1='0' y1='0' x2='8' y2='0' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='0' y1='0' x2='0' y2='8' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='119' y1='0' x2='111' y2='0' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='119' y1='0' x2='119' y2='8' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='0' y1='119' x2='8' y2='119' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='0' y1='119' x2='0' y2='111' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='119' y1='119' x2='111' y2='119' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Cline x1='119' y1='119' x2='119' y2='111' stroke='white' stroke-opacity='0.3' stroke-width='1'/%3E%3Ccircle cx='24' cy='24' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='48' cy='24' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='72' cy='24' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='96' cy='24' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='24' cy='48' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='48' cy='48' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='72' cy='48' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='96' cy='48' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='24' cy='72' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='48' cy='72' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='72' cy='72' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='96' cy='72' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='24' cy='96' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='48' cy='96' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='72' cy='96' r='1' fill='white' fill-opacity='0.15'/%3E%3Ccircle cx='96' cy='96' r='1' fill='white' fill-opacity='0.15'/%3E%3C/svg%3E");
          background-repeat: repeat;
          background-size: 120px 120px;
        }
        .beyond-photo { cursor: grab; }
        .beyond-photo:active { cursor: grabbing; }
      `}</style>
      <div className="beyond-grid" />
      <div className="beyond-crosshairs" />

      {/* Scattered draggable photos */}
      {imgs.map((img, i) => (
        <img
          key={img.file}
          ref={el => { imgRefs.current[i] = el; }}
          src={`/photos/${img.file}`}
          alt=""
          draggable={false}
          loading="lazy"
          className="beyond-photo"
          onMouseDown={e => startDrag(e, i, img)}
          onMouseEnter={() => setHoveredIdx(i)}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{
            position: 'absolute',
            left: img.x,
            top: img.y,
            width: '180px',
            transform: hoveredIdx === i
              ? `rotate(${img.rot + (img.rot >= 0 ? 2.5 : -2.5)}deg) scale(1.06)`
              : `rotate(${img.rot}deg) scale(1)`,
            zIndex: hoveredIdx === i ? maxZ.current + 50 : img.z,
            boxShadow: hoveredIdx === i
              ? '0 16px 48px rgba(0,0,0,0.85), 0 4px 16px rgba(0,0,0,0.6)'
              : '0 8px 32px rgba(0,0,0,0.7), 0 2px 8px rgba(0,0,0,0.5)',
            borderRadius: '3px',
            display: 'block',
            willChange: 'transform',
            transition: 'transform 0.35s ease, box-shadow 0.35s ease',
          }}
        />
      ))}

    </div>

      {/* Big connect text — half inside grid, half outside (negative marginTop) */}
      <div style={{
        marginTop: '-90px',
        textAlign: 'center',
        position: 'relative',
        zIndex: 10,
      }}>
        <p style={{
          fontFamily: "'Aujournuit', Georgia, serif",
          fontSize: 'clamp(64px, 10vw, 140px)',
          fontWeight: 400,
          color: '#ffffff',
          letterSpacing: '-0.03em',
          lineHeight: 1.0,
          margin: 0,
          fontStyle: 'normal',
        }}>
          Let's connect
        </p>
        <p style={{
          fontFamily: "'Aujournuit', Georgia, serif",
          fontSize: 'clamp(28px, 4vw, 56px)',
          fontWeight: 400,
          color: COLORS.accent,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          margin: '8px 0 0 0',
          fontStyle: 'italic',
        }}>
          and create something meaningful.
        </p>
      </div>
    </div>
  );
}

function CabinetSection() {
  const [ref, visible] = useFadeIn();
  const [hoveredEd, setHoveredEd] = useState(null);

  const skillGroups = [
    { label: 'Research', skills: ['UserTesting', 'Google Forms', 'Perplexity'] },
    { label: 'Design and Prototyping', skills: ['Figma', 'Protopie', 'Framer', 'Lottie'] },
    { label: 'Thinking and Systems', skills: ['Miro', 'FigJam', 'Notion'] },
    { label: 'AI Tools', skills: ['ChatGPT', 'Claude', 'Midjourney', 'Galileo AI', 'Uizard'] },
  ];

  const education = [
    {
      initials: 'NID',
      degree: 'M.Des Design for Retail Experience',
      institution: 'National Institute of Design',
      years: '2024 – 26',
    },
    {
      initials: 'DU',
      degree: 'B.A. Psychology Honours',
      institution: 'Daulat Ram College, University of Delhi',
      years: '2020 – 23',
    },
    {
      initials: 'SA',
      degree: 'Graphic Design Diploma',
      institution: 'Sri Aurobindo Centre for Arts and Creativity',
      years: '2022 – 23',
    },
  ];

  const labelStyle = {
    fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontSize: '10px',
    fontWeight: 400,
    letterSpacing: '0.12em',
    color: 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    marginBottom: '10px',
  };

  const bodyTextStyle = {
    fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    fontWeight: 300,
    fontSize: 'clamp(13px, 1.3vw, 15px)',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.75,
    letterSpacing: '0.005em',
    margin: 0,
  };

  return (
    <section
      id="about"
      ref={ref}
      style={{
        background: '#0b0a09',
        padding: '120px 40px 160px',
        borderTop: `1px solid ${COLORS.border}`,
      }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Centred heading above columns */}
        <div style={{ ...fadeUp(visible, 0), textAlign: 'center', marginBottom: '80px' }}>
          <h2 style={{
            fontFamily: "'Aujournuit', Georgia, serif",
            fontWeight: 300,
            fontSize: 'clamp(36px, 5vw, 72px)',
            color: COLORS.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: 0,
          }}>
            About{' '}
            <em style={{
              fontStyle: 'italic',
              fontWeight: 300,
              color: '#ffffff',
              textShadow: '0 0 18px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.15), 0 0 80px rgba(210,225,255,0.08), 0 0 140px rgba(200,215,255,0.05)',
            }}>Her</em>
          </h2>
          <p style={{
            fontFamily: "'Aujournuit', Georgia, serif",
            fontStyle: 'italic',
            fontWeight: 300,
            fontSize: 'clamp(16px, 1.8vw, 22px)',
            color: '#D81060',
            margin: '24px 0 0 0',
            letterSpacing: '-0.01em',
            lineHeight: 1.5,
          }}>
            "I don't just design how things look. I design how they feel, behave, and stay with you"
          </p>
        </div>

        <div className="about-grid">

          {/* ── Left column: text content ── */}
          <div style={{ ...fadeUp(visible, 0.1), display: 'flex', flexDirection: 'column', gap: '36px', justifyContent: 'center', alignSelf: 'center' }}>

            {/* Subline */}
            <p style={{
              fontFamily: "'Aujournuit', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 300,
              fontSize: 'clamp(16px, 1.8vw, 22px)',
              color: 'rgba(255,255,255,0.5)',
              margin: 0,
              letterSpacing: '-0.01em',
              lineHeight: 1.4,
            }}>
              Hey! I am Priyashree Acharya
            </p>

            {/* Body paragraphs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {[
                'I\'m an experience designer working across design, psychology, and technology. I\'m drawn to understanding how people interact with the world around them and using design to shape experiences that feel intuitive, thoughtful, and human.',
                'My background in psychology influences the way I observe and ask questions, but I\'m equally interested in systems, storytelling, visual thinking, and the small details that shape an experience over time.',
                'I tend to approach projects with curiosity first, exploring different possibilities, staying adaptable, and learning through the process. For me, design is less about arriving at quick answers and more about understanding people, context, and the feeling an experience leaves behind.',
              ].map((para, i) => (
                <p key={i} style={bodyTextStyle}>{para}</p>
              ))}
            </div>

            {/* Resume button */}
            <div style={{ marginTop: '8px' }}>
              <RainbowGlowButton
                href="https://drive.google.com/file/d/1AtIKLqEMifcdoJTZFlQnJaTEwWQPaPQ6/view?usp=sharing"
                target="_blank"
                rel="noopener noreferrer"
              >
                See Resume
              </RainbowGlowButton>
            </div>

          </div>

          {/* ── Right column: photo ── */}
          <div style={{ ...fadeUp(visible, 0), display: 'flex', flexDirection: 'column', gap: '56px', alignItems: 'center', position: 'relative' }}>

            {/* Pink blob */}
            <div style={{
              position: 'absolute',
              top: '10%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '340px',
              height: '340px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(216,16,96,0.75) 0%, transparent 70%)',
              filter: 'blur(48px)',
              pointerEvents: 'none',
              zIndex: 0,
            }} />

            {/* Portrait */}
            <img
              src="/about-photo.webp"
              alt="Priyashree Acharya"
              loading="lazy"
              style={{
                width: '60%',
                display: 'block',
                objectFit: 'contain',
                position: 'relative',
                zIndex: 1,
              }}
            />

          </div>
        </div>

        {/* My Journey section */}
        <div style={{ ...fadeUp(visible, 0.2), textAlign: 'center', marginTop: '120px' }}>
          <h2 style={{
            fontFamily: "'Aujournuit', Georgia, serif",
            fontWeight: 300,
            fontSize: 'clamp(32px, 4vw, 56px)',
            color: COLORS.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: 0,
          }}>
            My <em style={{ fontStyle: 'italic', color: '#ffffff', textShadow: '0 0 18px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.15), 0 0 80px rgba(210,225,255,0.08), 0 0 140px rgba(200,215,255,0.05)' }}>journey so far</em>
          </h2>
        </div>

        {/* Education 3-column grid */}
        <div style={{
          ...fadeUp(visible, 0.25),
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '48px',
          marginTop: '72px',
          textAlign: 'center',
        }}>
          {[
            {
              degree: 'Psychology Honours',
              institution: 'Daulat Ram College\nUniversity of Delhi',
              years: '2020 – 23',
              logo: '/psychologyhons.webp',
            },
            {
              degree: 'Graphic Design Diploma',
              institution: 'Sri Aurobindo Centre\nfor Arts and Creativity',
              years: '2022 – 23',
              logo: '/sacacsmol.webp',
            },
            {
              degree: 'Design for Retail Experience',
              institution: 'National Institute\nof Design',
              years: '2024 – 26',
              logo: '/nidlogofull.webp',
            },
          ].map((ed, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}
              onMouseEnter={() => setHoveredEd(i)}
              onMouseLeave={() => setHoveredEd(null)}
            >
              {/* Institution logo */}
              <div style={{ width: '160px', height: '72px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={ed.logo} alt={ed.institution} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </div>
              {/* Degree */}
              <div style={{
                fontFamily: "'Aujournuit', Georgia, serif",
                fontStyle: 'italic',
                fontWeight: 300,
                fontSize: '14px',
                color: hoveredEd === i ? COLORS.accent : 'rgba(255,255,255,0.85)',
                lineHeight: 1.4,
                transition: 'color 0.25s ease',
              }}>
                {ed.degree}
              </div>
              {/* Institution + year */}
              <div style={{
                fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                fontWeight: 300,
                fontSize: '11px',
                color: 'rgba(255,255,255,0.4)',
                lineHeight: 1.6,
                letterSpacing: '0.01em',
                whiteSpace: 'pre-line',
              }}>
                {ed.institution}{'\n'}{ed.years}
              </div>
            </div>
          ))}
        </div>

        {/* My Design Ethos */}
        <div style={{ ...fadeUp(visible, 0.3), textAlign: 'center', marginTop: '120px' }}>
          <h2 style={{
            fontFamily: "'Aujournuit', Georgia, serif",
            fontWeight: 300,
            fontSize: 'clamp(32px, 4vw, 56px)',
            color: COLORS.textPrimary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            margin: 0,
          }}>
            My Design <em style={{ fontStyle: 'italic', color: '#ffffff', textShadow: '0 0 18px rgba(255,255,255,0.3), 0 0 40px rgba(255,255,255,0.15), 0 0 80px rgba(210,225,255,0.08), 0 0 140px rgba(200,215,255,0.05)' }}>Ethos</em>
          </h2>
        </div>

        {/* Skill circles */}
        <div style={{
          ...fadeUp(visible, 0.35),
          display: 'flex',
          justifyContent: 'center',
          gap: '40px',
          marginTop: '64px',
          flexWrap: 'wrap',
        }}>
          {['Behavioural\nInsight', 'Storytelling', 'Human Centered\nDesign', 'Product\nThinking'].map((label, i) => (
            <SkillCircle key={i} label={label} />
          ))}
        </div>


        <BeyondDesignSection />

      </div>

      <style>{`
        .about-grid {
          display: grid;
          grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
          gap: 80px;
          align-items: center;
        }
        @media (max-width: 768px) {
          .about-grid {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
        }
      `}</style>
    </section>
  );
}

// ─── FOLDER GALLERY ──────────────────────────────────────────────────────────

function PageCard({ page, open, folderHovered, CARD_W, CARD_H, index }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);

  function handleMouseMove(e) {
    if (!open) return;
    const rect = cardRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = (e.clientX - cx) / (rect.width / 2);
    const dy = (e.clientY - cy) / (rect.height / 2);
    setTilt({ x: -dy * 10, y: dx * 10 });
  }

  function handleMouseLeave() {
    setTilt({ x: 0, y: 0 });
    setHovered(false);
  }

  // Closed: stacked tightly over the folder, each slightly rotated
  const closedRot = (index - 2.5) * 2.2;
  // All transforms include rotate(90deg) to display cards in landscape orientation
  // Peek: all cards stacked in one pile, slight rotation variation to show depth
  const peekX = (index - 2.5) * 4;
  const peekY = -75;
  const peekRot = (index - 2.5) * 5;
  const openT   = `translate(calc(-50% + ${page.openX}px), calc(-50% + ${page.openY}px)) rotate(${page.rot}deg)`;
  const peekT   = `translate(calc(-50% + ${peekX}px), calc(-50% + ${peekY}px)) rotate(90deg) rotate(${peekRot}deg) scale(0.78)`;
  const closedT = `translate(-50%, -50%) rotate(90deg) rotate(${closedRot}deg) scale(0.52)`;

  const currentT = open ? openT : (folderHovered ? peekT : closedT);
  // All non-open cards sit between folder back (z:5) and folder front (z:25) → appear inside
  const zIdx = open ? 30 + index : 6 + index;

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: `${CARD_W}px`,
        height: `${CARD_H}px`,
        transform: currentT,
        transition: `transform 0.55s cubic-bezier(0.34, 1.28, 0.64, 1)`,
        zIndex: zIdx,
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <a
        href={page.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => { if (!open) e.preventDefault(); }}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
          borderRadius: '10px',
          overflow: 'hidden',
          boxShadow: hovered && open
            ? '0 28px 64px rgba(0,0,0,0.55), 0 6px 18px rgba(0,0,0,0.3)'
            : '0 10px 32px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.25)',
          transform: `perspective(800px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
          transition: hovered ? 'box-shadow 0.3s ease' : 'transform 0.5s ease, box-shadow 0.3s ease',
          cursor: open ? 'pointer' : 'default',
          willChange: 'transform',
        }}
      >
        <img
          src={page.src}
          alt=""
          draggable={false}
          style={{ width: '100%', height: '100%', objectFit: 'contain', objectPosition: 'center', display: 'block', pointerEvents: 'none', userSelect: 'none', background: '#f2ede4' }}
        />
      </a>
    </div>
  );
}

function FolderGallery() {
  const [open, setOpen] = useState(false);
  const [folderHovered, setFolderHovered] = useState(false);

  const CARD_W = 210;
  const CARD_H = 278;

  // Open positions relative to folder center (negative Y = upward).
  // 2 rows × 3 cols messy layout matching the reference diagram exactly.
  const pages = [
    { id: 1, src: '/psychology/1.webp', url: 'https://drive.google.com/file/d/1iOdpbqaL0MLfuhhJ6FwsmD7MoI4_dgdY/view?usp=sharing', openX: -240, openY: -480, rot: -8  },
    { id: 2, src: '/psychology/2.webp', url: 'https://drive.google.com/file/d/1NTU44_4TEbIyCqDCImTANk1ukp7DdY4h/view?usp=sharing', openX:    0, openY: -500, rot:  3  },
    { id: 3, src: '/psychology/3.webp', url: 'https://drive.google.com/file/d/197iOkW6lBwX59V1sR8FFRSOx_50CRFBs/view?usp=sharing', openX:  245, openY: -472, rot:  9  },
    { id: 4, src: '/psychology/4.webp', url: 'https://drive.google.com/file/d/1-UX3a7GcXSuV4V4Rmq20tDxUB94ulkOp/view?usp=sharing', openX: -248, openY: -195, rot: -11 },
    { id: 5, src: '/psychology/5.webp', url: 'https://drive.google.com/file/d/1Hv462TFQ88T5FI7oFUWWII87fhrlRnRe/view?usp=sharing', openX:   -8, openY: -190, rot:  -2 },
    { id: 6, src: '/psychology/6.webp', url: 'https://drive.google.com/file/d/1hST_r-zNfYeoC7O2keK3n9TFmNkTShny/view?usp=sharing', openX:  248, openY: -175, rot:   8 },
  ];

  // Folder is 3× original size: 420×330px
  const FOLDER_W = 420;
  const FOLDER_H = 330;

  return (
    <section id="theory" style={{
      background: COLORS.bg,
      position: 'relative',
      minHeight: '960px',
      borderTop: `1px solid ${COLORS.border}`,
    }}>
      {/* Section header */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '120px 80px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '20px' }}>
          <SectionLabel>THEORY</SectionLabel>
          <h2 style={{
            fontFamily: "'Aujournuit', Georgia, serif",
            fontWeight: 400,
            fontSize: 'clamp(36px, 5vw, 64px)',
            color: COLORS.textPrimary,
            lineHeight: 1.12,
            margin: 0,
            letterSpacing: '-0.01em',
          }}>
            What was I doing <GlowWord>before</GlowWord> design?
          </h2>
          <p style={{
            fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
            fontWeight: 300,
            fontSize: '14px',
            color: COLORS.textSecondary,
            lineHeight: 1.8,
            margin: 0,
            maxWidth: '520px',
          }}>
            Design shaped by behavioral psychology and real conversations. Understanding people is where it begins.
          </p>
        </div>
      </div>

      {/* Folder + cards — folder anchored to bottom-center */}
      <div style={{
        position: 'absolute',
        bottom: '60px',
        left: '50%',
        marginLeft: `-${FOLDER_W / 2}px`,
        width: `${FOLDER_W}px`,
        height: `${FOLDER_H}px`,
      }}>
        {/* Folder BACK — renders below cards so cards appear inside */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none' }}>
          <svg viewBox="0 0 420 330" width={FOLDER_W} height={FOLDER_H} fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="60" width="412" height="264" rx="36" fill="#c878e8"/>
            <path d="M4 66 Q4 44 28 44 L158 44 Q182 44 195 68 L208 88 L4 88 Z" fill="#c878e8"/>
          </svg>
        </div>

        {/* Cards — sandwiched between back (z:5) and front (z:25) */}
        {pages.map((page, i) => (
          <PageCard key={page.id} page={page} open={open} folderHovered={folderHovered} CARD_W={CARD_W} CARD_H={CARD_H} index={i} />
        ))}

        {/* Folder FRONT — covers lower half of cards, handles hover/click */}
        <div
          onClick={() => setOpen(o => !o)}
          onMouseEnter={() => { if (!open) setFolderHovered(true); }}
          onMouseLeave={() => setFolderHovered(false)}
          title={open ? 'Close folder' : 'Open folder'}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 25,
            cursor: 'pointer',
            transition: 'transform 0.4s cubic-bezier(0.34,1.3,0.64,1)',
            transform: open ? 'translateY(6px)' : 'translateY(0)',
          }}
        >
          <svg viewBox="0 0 420 330" width={FOLDER_W} height={FOLDER_H} fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="92" width="412" height="232" rx="36" fill="#f0abfc"/>
            <rect x="28" y="96" width="364" height="12" rx="6" fill="rgba(255,255,255,0.22)"/>
            <text x="210" y="230" textAnchor="middle" fontFamily="'Aujournuit', Georgia, serif" fontWeight="400" fontSize="22" fill="rgba(255,255,255,0.92)" letterSpacing="0.5">A Glimpse into</text>
            <text x="210" y="258" textAnchor="middle" fontFamily="'Aujournuit', Georgia, serif" fontWeight="400" fontSize="22" fill="rgba(255,255,255,0.92)" letterSpacing="0.5">Psychology Papers</text>
          </svg>
        </div>
      </div>

      {/* Click hint — below the folder */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
          userSelect: 'none',
          fontFamily: "'Neue Helvetica World', Helvetica, Arial, sans-serif",
          fontSize: '10px',
          letterSpacing: '0.2em',
          color: 'rgba(255,255,255,0.25)',
          textTransform: 'uppercase',
          transition: 'color 0.3s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
      >
        {open ? 'Click to close' : 'Click to open'}
      </div>
    </section>
  );
}

// ─── SANDBOX ─────────────────────────────────────────────────────────────────

function SandboxSection() {
  const [ref, visible] = useFadeIn();

  const experiments = [
    {
      label: 'Why concrete words stick and abstract ones don\'t',
      sub: 'Concrete word pairs hit 100% recall. Abstract ones dropped to 74.99% — how you say something changes whether people remember it.',
      tags: ['Paivio word pair list', 'Dual coding theory', 'Recall experiment'],
      url: 'https://docs.google.com/document/d/1cBzh3825bv5bK9d0H_P7jH_wmcmwiG1W-YG6cgBuiLI/edit',
    },
    {
      label: 'When colour fights the brain',
      sub: 'When visual information contradicts meaning, the brain slows and errors increase — a window into attention and automaticity.',
      tags: ['Stroop Test', 'Reaction time', 'Cognitive interference'],
      url: 'https://docs.google.com/document/d/1UnFdGajhx_QwbaVNGQN9HmtscaTfl-zR/edit?usp=sharing&ouid=117923889145803854754&rtpof=true&sd=true',
    },
    {
      label: 'Body image, filters and how we see ourselves',
      sub: '112 college students surveyed. 30% of females wanted to change their weight. 25% of males wanted to change nothing.',
      tags: ['Self-constructed questionnaire', 'Content analysis', 'Thematic analysis'],
      url: 'https://docs.google.com/document/d/1t7qYf1MzJk0FyOVRZphU1ASELNXKTSzCrhCaoMp4pfM/edit',
    },
    {
      label: 'Why age makes you care more about the planet',
      sub: '81 people, 3 age groups. Feeling connected to nature predicts eco-behaviour — middle adults were significantly more pro-environmental than adolescents.',
      tags: ['Nature connectedness scale', 'Ecological behavior', 'Correlation & ANOVA'],
      url: 'https://docs.google.com/document/d/1JjNFQxrIB3tctIh6h7j01euwegBWw-LdHQtHGh0aAOA/edit',
    },
    {
      label: 'What others see in you that you can\'t',
      sub: 'I ranked creativity 10th. Everyone I asked ranked it 1st or 2nd. A 7-day intervention on my lowest strength raised my flourishing score from 48 to 51.',
      tags: ['VIA Inventory of Strengths', 'Flourishing Scale', 'ABA intervention'],
      url: 'https://docs.google.com/document/d/1Q1Vk7zJyXcGL5CsnOIF8c88ZaHpFHp-wUbUNSgzMhws/edit',
    },
    {
      label: 'How we reason through impossible choices',
      sub: 'How people reason through moral dilemmas — from rule-following to principled thinking — when two values can\'t both be upheld.',
      tags: ['Kohlberg\'s theory', 'Moral reasoning', 'Stage-based assessment'],
      url: 'https://docs.google.com/document/d/1GAecQEpSJQoTQPgun8-uhQGbF8zL4RXs/edit',
    },
  ];

  return (
    <section
      id="sandbox"
      ref={ref}
      style={{
        background: COLORS.bg,
        padding: '120px 40px 40px',
        borderTop: `1px solid ${COLORS.border}`,
      }}
    >
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: '80px', gap: '20px' }}>
          <div style={fadeUp(visible, 0)}>
            <SectionLabel>THEORY</SectionLabel>
            <h2 style={{
              fontFamily: "'Aujournuit', Georgia, serif",
              fontWeight: 400,
              fontSize: 'clamp(36px, 5vw, 64px)',
              color: COLORS.textPrimary,
              lineHeight: 1.12,
              margin: 0,
              letterSpacing: '-0.01em',
            }}>
              What was I doing <GlowWord>before</GlowWord> design?
            </h2>
          </div>
          <div style={fadeUp(visible, 0.15)}>
            <p style={{
              fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
              fontWeight: 300,
              fontSize: '14px',
              color: COLORS.textSecondary,
              lineHeight: 1.8,
              margin: 0,
            }}>
              Design shaped by behavioral psychology and real conversations. Understanding people is where it begins.
            </p>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '28px',
        }}>
          {experiments.map((exp, i) => (
            <SandboxCard key={exp.label} exp={exp} delay={i * 0.07} visible={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SandboxCard({ exp, delay, visible }) {
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef(null);
  const [perimeter, setPerimeter] = useState(0);

  useEffect(() => {
    if (!cardRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const el = entry.target;
      setPerimeter(2 * (el.offsetWidth + el.offsetHeight));
    });
    ro.observe(cardRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <a href={exp.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit', display: 'contents' }}>
    <div
      ref={cardRef}
      style={{
        ...fadeUp(visible, delay),
        background: hovered ? COLORS.surface : COLORS.bg,
        padding: '40px 32px',
        cursor: 'pointer',
        transition: 'background 0.3s ease, opacity 0.8s ease, transform 0.8s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        minHeight: '160px',
        position: 'relative',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Trim path stroke border */}
      {perimeter > 0 && (
        <svg
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.15s ease',
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <filter id="stroke-glow">
              <feGaussianBlur stdDeviation="1.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <rect
            x="1"
            y="1"
            width="99%"
            height="99%"
            fill="none"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="0.6"
            filter="url(#stroke-glow)"
            strokeDasharray={perimeter}
            strokeDashoffset={hovered ? 0 : perimeter}
            style={{
              transition: hovered ? 'stroke-dashoffset 0.5s ease' : 'none',
            }}
          />
        </svg>
      )}
      <div style={{
        width: '24px',
        height: '1px',
        background: hovered ? COLORS.accent : COLORS.textVeryMuted,
        transition: 'background 0.3s ease, width 0.3s ease',
        ...(hovered ? { width: '40px' } : {}),
      }} />
      <h3 style={{
        fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontWeight: 300,
        fontSize: '18px',
        color: hovered ? COLORS.textPrimary : '#918e87',
        margin: 0,
        letterSpacing: '-0.01em',
        lineHeight: 1.4,
        transition: 'color 0.3s ease',
      }}>
        {exp.label}
      </h3>
      <p style={{
        fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontWeight: 300,
        fontSize: '11px',
        color: hovered ? '#7a7870' : COLORS.textMuted,
        margin: 0,
        letterSpacing: '0.05em',
        lineHeight: 1.6,
        transition: 'color 0.3s ease',
      }}>
        {exp.sub}
      </p>
      {exp.tags && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
          {exp.tags.map(tag => (
            <span key={tag} style={{
              fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              fontSize: '10px',
              letterSpacing: '0.04em',
              color: hovered ? 'rgba(255,255,255,0.75)' : COLORS.textMuted,
              border: `1px solid ${COLORS.border}`,
              padding: '3px 10px',
              transition: 'color 0.3s ease',
            }}>
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
    </a>
  );
}

// ─── CONTACT / FOOTER ────────────────────────────────────────────────────────

function RingCarousel() {
  const cardRefs = useRef([]);
  const angleRef = useRef(0);
  const lastRef = useRef(null);
  const rafRef = useRef(null);

  const N = RING_CAROUSEL_CARDS.length;
  const CARD_W = 320;
  const CARD_H = 200;
  // Elliptical arc: tighter horizontal spread, shallower vertical
  const RADIUS_X = 560;   // tighter horizontal spread
  const RADIUS_Y = 300;   // vertical rise
  const SPEED = 8;
  // ARC_HALF = 100° shows 5 of 8 cards (0°, ±45°, ±90° at the fade boundary)
  const ARC_HALF = 100;

  useEffect(() => {
    function tick(t) {
      const dt = lastRef.current !== null ? (t - lastRef.current) / 1000 : 0;
      lastRef.current = t;
      angleRef.current = (angleRef.current + SPEED * dt) % 360;

      RING_CAROUSEL_CARDS.forEach((_, i) => {
        const el = cardRefs.current[i];
        if (!el) return;

        const base = (360 / N) * i;
        const total = (base + angleRef.current) % 360;
        // 0° = top (center), ±90° = sides, ±180° = bottom (hidden)
        const norm = total > 180 ? total - 360 : total;
        const rad = (norm * Math.PI) / 180;

        // Elliptical position: wide X, shallower Y
        const x = RADIUS_X * Math.sin(rad);
        const y = -RADIUS_Y * Math.cos(rad);

        // Card tilts to match its arc position — fans outward from center
        const cardRot = norm * 0.85;

        // Fade zone in last 22° before boundary
        const FADE_START = ARC_HALF - 22;
        const inArc = Math.abs(norm) <= ARC_HALF;
        const opacity = !inArc ? 0
          : Math.abs(norm) > FADE_START
          ? 1 - (Math.abs(norm) - FADE_START) / (ARC_HALF - FADE_START)
          : 1;

        // Scale: center card 100%, side cards shrink to ~55%
        const scale = 0.55 + 0.45 * Math.cos(rad);

        // Z-order: front card always on top
        const zIdx = Math.round(Math.cos(rad) * 10 + 10);

        el.style.transform = `translate(${x}px, ${y}px) rotate(${cardRot}deg) scale(${scale})`;
        el.style.opacity = String(Math.max(0, opacity));
        el.style.zIndex = String(zIdx);
      });

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [N]);

  return (
    <section style={{
      background: COLORS.bg,
      height: '640px',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Arc origin sits at bottom-center; cards fan upward in a wide ellipse */}
      <div style={{
        position: 'absolute',
        bottom: '80px',
        left: '50%',
        width: 0,
        height: 0,
      }}>
        {RING_CAROUSEL_CARDS.map((card, i) => (
          <div
            key={i}
            ref={el => { cardRefs.current[i] = el; }}
            style={{
              position: 'absolute',
              width: `${CARD_W}px`,
              height: `${CARD_H}px`,
              left: `-${CARD_W / 2}px`,
              top: `-${CARD_H / 2}px`,
              borderRadius: '20px',
              overflow: 'hidden',
              background: card.gradient,
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 28px 72px rgba(0,0,0,0.72), 0 6px 20px rgba(0,0,0,0.45)',
              willChange: 'transform, opacity',
            }}
          >
            {card.image
              ? <>
                  <img src={card.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: card.darken ? 'brightness(0.65) saturate(0.8)' : 'brightness(0.88)' }} />
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)' }} />
                </>
              : <div style={{
                  position: 'absolute', inset: 0,
                  background: 'radial-gradient(ellipse 72% 55% at 35% 30%, rgba(255,255,255,0.09) 0%, transparent 100%)',
                }} />
            }
          </div>
        ))}
      </div>

      {/* "Create Together?" + CTA — anchored to bottom */}
      <div style={{
        position: 'absolute',
        bottom: '32px',
        left: '50%',
        transform: 'translateX(-50%)',
        textAlign: 'center',
        zIndex: 30,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        whiteSpace: 'nowrap',
      }}>
        <span style={{
          fontFamily: "'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif",
          fontSize: '10px',
          letterSpacing: '0.2em',
          color: COLORS.textSecondary,
          textTransform: 'uppercase',
          display: 'block',
        }}>/ Let's Connect</span>
        <h2 style={{
          fontFamily: "'Aujournuit', 'Neue Helvetica World', Helvetica, sans-serif",
          fontSize: '120px',
          fontWeight: 400,
          color: COLORS.textPrimary,
          lineHeight: 0.9,
          margin: 0,
          letterSpacing: '-0.01em',
        }}>
          <em style={{ fontStyle: 'italic' }}>Create</em><br />Together?
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <RainbowGlowButton href="/about">Know her</RainbowGlowButton>
          <a
            href="mailto:Priyashree@nid.edu"
            style={{
              fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
              fontWeight: 300, fontSize: '13px',
              color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em',
              textDecoration: 'none', transition: 'color 0.3s ease',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#ffffff'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
          >Priyashree@nid.edu</a>
        </div>
      </div>

    </section>
  );
}

function ContactSection() {
  const [ref] = useFadeIn();

  const footerLinks = ['About', 'Work', 'Logs', 'Cabinet', 'Sandbox', 'Resume'];
  const socialLinks = ['LinkedIn', 'Twitter', 'Instagram'];

  return (
    <section
      id="contact"
      ref={ref}
      style={{
        background: '#0b0a09',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top blend gradient from section above */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '180px',
        background: `linear-gradient(to bottom, ${COLORS.bg} 0%, transparent 100%)`,
        zIndex: 2,
        pointerEvents: 'none',
      }} />



      {/* Footer bottom */}
      <div style={{
        padding: '48px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        position: 'relative',
        zIndex: 1,
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '32px',
      }}>
        {/* Name + copyright */}
        <div>
          <div style={{
            fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontWeight: 300,
            fontSize: '16px',
            color: '#ffffff',
            marginBottom: '6px',
            letterSpacing: '0.02em',
          }}>
            Priyashree Acharya
</div>
          <div style={{
            fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
            fontSize: '10px',
            color: 'rgba(255,255,255,0.3)',
            letterSpacing: '0.05em',
          }}>
            © 2026
          </div>
        </div>

        {/* Social icons */}
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          {[
            { href: 'mailto:priyashree.a@nid.edu', label: 'Gmail', src: '/gmails.webp' },
            { href: 'https://www.linkedin.com/in/priyashree02', label: 'LinkedIn', src: '/linkdn.webp' },
            { href: 'https://medium.com/@priyashreeacharya2002', label: 'Medium', src: '/medium.webp' },
            { href: 'https://www.behance.net/priyashacharya', label: 'Behance', src: '/behance.webp' },
          ].map(({ href, label, src }) => (
            <a
              key={label}
              href={href}
              target={label === 'Gmail' ? '_self' : '_blank'}
              rel="noopener noreferrer"
              aria-label={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                opacity: 0.4,
                transition: 'opacity 0.25s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '1'}
              onMouseLeave={e => e.currentTarget.style.opacity = '0.4'}
            >
              <img src={src} alt={label} style={{ width: '22px', height: '22px', objectFit: 'contain' }} />
            </a>
          ))}
        </div>

        {/* Vibecoded */}
        <div style={{
          fontFamily: "'Aujournuit', Georgia, serif",
          fontWeight: 300,
          fontStyle: 'italic',
          fontSize: '15px',
          color: 'rgba(255,255,255,0.75)',
          letterSpacing: '0.04em',
          textShadow: '0 0 18px rgba(255,255,255,0.4), 0 0 40px rgba(200,210,255,0.2)',
        }}>
          <>No templates were harmed<br />Vibe-coded into Reality</>
        </div>

      </div>
    </section>
  );
}

function FooterLink({ label }) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href="#"
      style={{
        fontFamily: "'Neue Helvetica World', 'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontSize: '9px',
        letterSpacing: '0.12em',
        color: hovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.35)',
        textDecoration: 'none',
        transition: 'color 0.3s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={e => e.preventDefault()}
    >
      {label.toUpperCase()}
    </a>
  );
}

// ─── SPLASH SCREEN ───────────────────────────────────────────────────────────

const GREETINGS = [
  { word: 'Hello',       lang: 'English'  },
  { word: 'नमस्ते',      lang: 'Hindi'    },
  { word: 'ನಮಸ್ಕಾರ',    lang: 'Kannada'  },
  { word: 'வணக்கம்',    lang: 'Tamil'    },
  { word: 'নমস্কার',     lang: 'Bengali'  },
  { word: 'سلام',        lang: 'Arabic'   },
  { word: 'Hallo',       lang: 'German'   },
  { word: 'Bonjour',     lang: 'French'   },
];

const HOLD = 220;   // ms each greeting is fully visible
const XFADE = 120;  // ms fade transition

function SplashScreen({ onDone }) {
  const [index, setIndex] = useState(0);
  const [wordVisible, setWordVisible] = useState(true);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    let current = 0;
    const timers = [];

    const showNext = () => {
      setWordVisible(false);
      timers.push(setTimeout(() => {
        current += 1;
        if (current < GREETINGS.length) {
          setIndex(current);
          setWordVisible(true);
          timers.push(setTimeout(showNext, HOLD));
        } else {
          timers.push(setTimeout(() => {
            setFading(true);
            setTimeout(onDone, 700);
          }, 1600));
        }
      }, XFADE));
    };

    timers.push(setTimeout(showNext, HOLD));
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  const { word } = GREETINGS[index];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: '#000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      opacity: fading ? 0 : 1,
      transition: 'opacity 0.7s ease',
      pointerEvents: fading ? 'none' : 'all',
    }}>
      <style>{`
        @keyframes splashShine {
          0%   { background-position: 100% center; }
          100% { background-position: 0% center; }
        }
        .splash-word {
          font-family: 'Aujournuit', Georgia, serif;
          font-weight: 400;
          font-size: clamp(18px, 3.5vw, 40px);
          letter-spacing: -0.02em;
          line-height: 1;
          text-align: center;
          background: linear-gradient(
            90deg,
            #D81060 20%,
            #D81060 36%,
            #f07090 46%,
            #fcd5de 50%,
            #f07090 54%,
            #D81060 64%,
            #D81060 80%
          );
          background-size: 400% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: splashShine 4.5s linear 0s 1 both;
        }
        .splash-thanks {
          font-family: 'Ronzino', 'Neue Helvetica World', Helvetica, sans-serif;
          font-weight: 400;
          font-size: clamp(13px, 1.8vw, 22px);
          letter-spacing: 0.04em;
          line-height: 1.3;
          text-align: center;
          background: linear-gradient(
            90deg,
            #D81060 20%,
            #D81060 36%,
            #f07090 46%,
            #fcd5de 50%,
            #f07090 54%,
            #D81060 64%,
            #D81060 80%
          );
          background-size: 400% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: splashShine 4.5s linear 0.5s 1 both;
        }
      `}</style>

      {/* Cycling hello */}
      <div style={{
        opacity: wordVisible ? 1 : 0,
        transition: `opacity ${XFADE}ms ease`,
        textAlign: 'center',
        marginBottom: '20px',
      }}>
        <div key={index} className="splash-word">{word}</div>
      </div>

      {/* Static tagline below */}
      <div className="splash-thanks">Thanks for being here.</div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────────────

function StarCursor() {
  const cursorRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const rafRef = useRef(null);

  useEffect(() => {
    const el = cursorRef.current;
    if (!el) return;

    const onMove = (e) => {
      pos.current = { x: e.clientX, y: e.clientY };
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(() => {
          el.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px) translate(-50%, -50%) rotate(-20deg)`;
          rafRef.current = null;
        });
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });

    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div ref={cursorRef} style={{
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: 99999,
      fontSize: '18px',
      color: '#ffffff',
      WebkitTextStroke: '0.5px #D81060',
      transform: 'translate(-100px, -100px) translate(-50%, -50%) rotate(-20deg)',
      userSelect: 'none',
      lineHeight: 1,
      left: 0,
      top: 0,
      willChange: 'transform',
    }}>✦</div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function useGlobalSetup() {
  useEffect(() => {
    const existing = document.querySelector('link[data-portfolio-fonts]');
    if (!existing) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.setAttribute('data-portfolio-fonts', 'true');
      link.href = 'https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400&display=swap';
      document.head.appendChild(link);
    }
    const existingFont = document.querySelector('style[data-blackletter]');
    if (!existingFont) {
      const style = document.createElement('style');
      style.setAttribute('data-blackletter', 'true');
      style.textContent = `
        @font-face {
          font-family: 'Blackletter';
          src: url('/BLACEB__.TTF') format('truetype');
          font-weight: normal;
          font-style: normal;
        }
      `;
      document.head.appendChild(style);
    }
    document.documentElement.style.scrollBehavior = 'smooth';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.background = COLORS.bg;
    document.body.style.overflowX = 'hidden';
    return () => { document.documentElement.style.scrollBehavior = ''; };
  }, []);
}

function HomePage() {
  useGlobalSetup();
  const [showSplash, setShowSplash] = useState(
    () => !sessionStorage.getItem('splashSeen')
  );
  const [splashDone, setSplashDone] = useState(
    () => !!sessionStorage.getItem('splashSeen')
  );
  const handleSplashDone = useCallback(() => {
    sessionStorage.setItem('splashSeen', 'true');
    setShowSplash(false);
    setSplashDone(true);
  }, []);

  return (
    <>
      {showSplash && <SplashScreen onDone={handleSplashDone} />}
      <div style={{
        background: COLORS.bg,
        minHeight: '100vh',
        visibility: splashDone ? 'visible' : 'hidden',
      }}>
        <Nav />
        <VideoHero />
        <WorkSection />
        <Hero />
        <DigestsSection />
        <FolderGallery />
        <RingCarousel />
        <ContactSection />
      </div>
    </>
  );
}

function AboutPage() {
  useGlobalSetup();
  return (
    <>
      <div style={{ background: COLORS.bg, minHeight: '100vh' }}>
        <Nav />
        <CabinetSection />
        <ContactSection />
      </div>
    </>
  );
}

function MobileWarning() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  if (!isMobile) return null;
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: '#0E0C0A',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px', textAlign: 'center',
      fontFamily: '"Inter", system-ui, sans-serif',
      color: '#EDEAE3',
    }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '16px', lineHeight: 1.3 }}>
        This website is not mobile friendly.
      </h1>
      <p style={{ fontSize: '16px', color: '#6B6860', lineHeight: 1.6, maxWidth: '320px' }}>
        Please view it on a desktop or laptop for the best experience.
      </p>
    </div>
  );
}


export default function App() {
  return (
    <BrowserRouter>
      <MobileWarning />
      <StarCursor />
      <ScrollToTop />
      <Suspense fallback={<div style={{ background: '#0E0C0A', minHeight: '100vh' }} />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/work/carbon-count" element={<CarbonCountPage />} />
          <Route path="/work/paradyes" element={<ParadyesPage />} />
          <Route path="/work/bunav" element={<BunavPage />} />
          <Route path="/work/ember" element={<EmberPage />} />
          <Route path="/work/anvaya" element={<AnvayaPage />} />
          <Route path="/work/ferrari-ar" element={<FerrariPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
